import React, { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, QrCode, Save, Check, Search, Truck } from 'lucide-react';
import { toast } from 'sonner';

const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const fmtData = (d) => d ? d.split('-').reverse().join('/') : '-';

function ConfigFreteCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/financeiro/fretes/config').then(r => setCfg({ ...r.data, valor: String(r.data.valor).replace('.', ',') })).catch(() => toast.error('Erro ao carregar configuração do frete')); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/financeiro/fretes/config', cfg);
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  if (!cfg) return <Card><CardContent className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></CardContent></Card>;
  const set = (k) => (e) => setCfg(p => ({ ...p, [k]: e.target.value }));

  return (
    <Card data-testid="card-config-frete">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><QrCode className="w-5 h-5 text-emerald-600" /> Frete de coleta e chave Pix</CardTitle>
        <CardDescription>Alterações valem só para os novos pedidos de coleta.{cfg.updated_by && ` Última alteração: ${cfg.updated_by}.`}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border">
          <div>
            <p className="font-medium text-sm">Cobrar frete nas coletas</p>
            <p className="text-xs text-slate-500">Desligado: nenhum cliente recebe Pix de frete.</p>
          </div>
          <Switch checked={cfg.ativo} onCheckedChange={(v) => setCfg(p => ({ ...p, ativo: v }))} data-testid="switch-frete-ativo" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Valor do frete (R$)</Label><Input value={cfg.valor} onChange={set('valor')} inputMode="decimal" data-testid="input-frete-valor" /></div>
          <div><Label>Chave Pix</Label><Input value={cfg.chave} onChange={set('chave')} placeholder="CNPJ, email, telefone ou chave aleatória" data-testid="input-frete-chave" /></div>
          <div><Label>Nome do recebedor</Label><Input value={cfg.nome} onChange={set('nome')} maxLength={25} data-testid="input-frete-nome" /></div>
          <div><Label>Cidade</Label><Input value={cfg.cidade} onChange={set('cidade')} data-testid="input-frete-cidade" /></div>
        </div>
        <p className="text-xs text-slate-500">Telefone como chave: use +55 e DDD (ex.: +5546999999999). CNPJ/CPF: somente números.</p>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-salvar-config-frete">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salvar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function RelatorioFretes({ onChanged }) {
  const [ano, setAno] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/financeiro/fretes/relatorio?ano=${ano}`).then(r => setData(r.data)).catch(() => toast.error('Erro ao carregar relatório de fretes'));
  }, [ano]);
  useEffect(() => { load(); }, [load]);

  const confirmar = async (p) => {
    if (!window.confirm(`Confirmar que o Pix de ${brl(p.valor)} (${p.numero} - ${p.cliente_nome}) caiu no extrato?`)) return;
    try {
      const r = await api.post(`/portal-cliente/backoffice/solicitacoes/${p.id}/pagamento`, { status: 'pago' });
      toast.success(r.data.message); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao confirmar'); }
  };

  if (!data) return <Card><CardContent className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></CardContent></Card>;
  const max = Math.max(1, ...data.meses.map(m => m.recebido));
  const anos = [0, 1, 2].map(i => new Date().getFullYear() - i);

  return (
    <Card data-testid="card-relatorio-fretes">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg"><Truck className="w-5 h-5 text-blue-600" /> Relatório de Fretes</CardTitle>
          <CardDescription>Valores recebidos por mês (pela data da confirmação) e Pix pendentes.</CardDescription>
        </div>
        <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
          <SelectTrigger className="w-28" data-testid="select-ano-fretes"><SelectValue /></SelectTrigger>
          <SelectContent>{anos.map(a => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-green-50 border border-green-200">
            <p className="text-xs text-green-700 font-medium">Recebido em {data.ano}</p>
            <p className="text-2xl font-bold text-green-900" data-testid="fretes-total-recebido">{brl(data.total_recebido)}</p>
          </div>
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700 font-medium">Pendente agora ({data.pendentes.length})</p>
            <p className="text-2xl font-bold text-amber-900" data-testid="fretes-total-pendente">{brl(data.total_pendente)}</p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-2 items-end h-40 keep-cols" data-testid="fretes-grafico-meses">
          {data.meses.map(m => (
            <div key={m.mes} className="flex flex-col items-center justify-end h-full gap-1" title={`${MESES[m.mes - 1]}: ${brl(m.recebido)} (${m.qtd_pagos} pago(s))`}>
              <span className="text-[10px] text-slate-600 tabular-nums">{m.recebido > 0 ? Number(m.recebido).toLocaleString('pt-BR') : ''}</span>
              <div className="w-full rounded-t bg-emerald-500/80 transition-all" style={{ height: `${(m.recebido / max) * 100}%`, minHeight: m.recebido > 0 ? 4 : 1 }} />
              <span className="text-[11px] text-slate-500">{MESES[m.mes - 1]}</span>
            </div>
          ))}
        </div>

        <div>
          <h4 className="font-semibold text-sm text-slate-800 mb-2">Clientes com Pix pendente</h4>
          {data.pendentes.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center border rounded-lg" data-testid="fretes-sem-pendentes">Nenhum frete pendente.</p>
          ) : (
            <div className="border rounded-lg divide-y" data-testid="fretes-lista-pendentes">
              {data.pendentes.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`frete-pendente-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-800 truncate">{p.cliente_nome}</p>
                    <p className="text-xs text-slate-500">{p.numero} · coleta {fmtData(p.data_preferencial)}{p.lembretes > 0 ? ` · ${p.lembretes} lembrete(s)` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={p.status_pagamento === 'informado' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                      {p.status_pagamento === 'informado' ? 'Cliente informou' : 'Pendente'}
                    </Badge>
                    <span className="font-semibold tabular-nums w-20 text-right">{brl(p.valor)}</span>
                    <Button size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-300" onClick={() => confirmar(p)} data-testid={`btn-frete-confirmar-${p.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Confirmar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ClientesCobranca() {
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');

  useEffect(() => { api.get('/financeiro/fretes/clientes').then(r => setClientes(r.data)).catch(() => toast.error('Erro ao carregar clientes')); }, []);

  const toggle = async (c, v) => {
    setClientes(prev => prev.map(x => x.id === c.id ? { ...x, cobrar_frete: v } : x));
    try {
      const r = await api.put(`/financeiro/fretes/clientes/${c.id}`, { cobrar_frete: v });
      toast.success(`${c.nome}: ${r.data.message.toLowerCase()}`);
    } catch (e) {
      setClientes(prev => prev.map(x => x.id === c.id ? { ...x, cobrar_frete: !v } : x));
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const lista = clientes.filter(c => c.nome.toLowerCase().includes(busca.toLowerCase()));
  const isentos = clientes.filter(c => !c.cobrar_frete).length;

  return (
    <Card data-testid="card-clientes-frete">
      <CardHeader>
        <CardTitle className="text-lg">Quem paga frete</CardTitle>
        <CardDescription>Desligue para isentar o cliente do frete nas coletas. {isentos > 0 && <b>{isentos} isento(s).</b>}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar cliente..." className="pl-9" data-testid="input-busca-cliente-frete" />
        </div>
        <div className="border rounded-lg divide-y max-h-[420px] overflow-y-auto">
          {lista.map(c => (
            <div key={c.id} className="flex items-center justify-between px-3 py-2">
              <span className="text-sm text-slate-800 truncate">{c.nome}</span>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs ${c.cobrar_frete ? 'text-emerald-700' : 'text-slate-400'}`}>{c.cobrar_frete ? 'Cobra' : 'Isento'}</span>
                <Switch checked={c.cobrar_frete} onCheckedChange={(v) => toggle(c, v)} data-testid={`switch-cobrar-frete-${c.id}`} />
              </div>
            </div>
          ))}
          {lista.length === 0 && <p className="text-sm text-slate-500 p-4 text-center">Nenhum cliente encontrado.</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export function FretesTab({ onChanged }) {
  return (
    <div className="space-y-4" data-testid="fretes-tab">
      <RelatorioFretes onChanged={onChanged} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ConfigFreteCard />
        <ClientesCobranca />
      </div>
    </div>
  );
}
